import videoUrlParser from 'js-video-url-parser';

/*
    Addresses YouTube itself hands out that the parser does not know: it reads
    nothing at all from them, so a block given one is left without a video.

    - `/shorts/<id>` is what the share button offers for an upright video
    - `/live/<id>` is what it offers for a stream or a premiere
    - `youtube-nocookie.com` is the host in the privacy enhanced embed code

    Each names a video the parser can read in another form, so they are rewritten
    on the way in rather than the parser being forked. Anything after the id is
    carried across, which is what keeps a start time on the URL working.
*/
const YOUTUBE_VIDEO_PATH = /youtube\.com\/(?:shorts|live)\/([\w-]+)\??/i;
const YOUTUBE_NOCOOKIE_HOST = /youtube-nocookie\.com/i;

const normalizeVideoUrl = (url) => String(url)
    .replace(YOUTUBE_NOCOOKIE_HOST, 'youtube.com')
    .replace(YOUTUBE_VIDEO_PATH, (match, id) => `youtube.com/watch?v=${id}${match.endsWith('?') ? '&' : ''}`);

// Unified function to parse video URLs and extract all necessary information
export const parseVideoUrl = (url) => {
    if (!url) return null;

    try {
        const parsed = videoUrlParser.parse(normalizeVideoUrl(url));

        /*
            A playlist or a channel is read as YouTube's without an id, and an id
            is the whole of what a player is built from. Answering with the
            provider alone leaves every caller to embed `.../embed/undefined`, so
            an address naming no single video is turned away here as one the
            parser could not read at all.
        */
        if (parsed && parsed.id) {
            return {
                provider: parsed.provider,
                id: parsed.id,
                params: parsed.params || {},
                mediaType: parsed.mediaType || 'video'
            };
        }
    } catch (error) {
        console.error('Error parsing video URL:', error);
    }

    return null;
};

// Extracts YouTube video ID from the given URL
export const getYtVideoId = (url) => {
    const parsed = parseVideoUrl(url);

    return parsed && parsed.provider === 'youtube' ? parsed.id : null;
};

// Extracts Vimeo video ID from the given URL
export const getVimeoVideoId = (url) => {
    const parsed = parseVideoUrl(url);

    return parsed && parsed.provider === 'vimeo' ? parsed.id : null;
};

// Extracts query parameters from a URL
export const extractQueryParams = (url) => {
    const parsed = parseVideoUrl(url);
    if (!parsed || !parsed.params) return {};

    const params = {};

    // Convert string values to appropriate types
    Object.keys(parsed.params).forEach((key) => {
        const value = parsed.params[key];
        if (value === 'true' || value === '1') {
            params[key] = true;
        } else if (value === 'false' || value === '0') {
            params[key] = false;
        } else if (!isNaN(value) && value !== '') {
            params[key] = parseInt(value, 10);
        } else {
            params[key] = value;
        }
    });

    return params;
};
